import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Space, Typography, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import api from '@/services/api.service';

const { Text } = Typography;

type Props = {
  token: string;
  defaultName?: string;
  alreadyAcknowledged?: boolean;
  acknowledgedByName?: string | null;
  onDone?: () => void;
};

/** Lightweight canvas signature pad for supplier portal acknowledgement. */
export default function SpotSupplierAckPanel({
  token,
  defaultName,
  alreadyAcknowledged,
  acknowledgedByName,
  onDone,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [name, setName] = useState(defaultName || '');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(Boolean(alreadyAcknowledged));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  const pos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      const t = e.touches[0];
      return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    }
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top };
  };

  const start = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const move = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const end = () => {
    drawing.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const submit = async () => {
    if (!name.trim()) {
      setError('Enter your name');
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSaving(true);
    setError(null);
    try {
      const blob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/png')
      );
      if (!blob) throw new Error('Could not capture signature');
      const form = new FormData();
      form.append('acknowledged_by_name', name.trim());
      if (notes.trim()) form.append('notes', notes.trim());
      form.append('signature', blob, 'supplier-ack-signature.png');
      for (const f of files) {
        const raw = f.originFileObj;
        if (raw) form.append('documents', raw as File);
      }
      await api.post(`/procurement/public/spot-purchase/${token}/acknowledgement`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDone(true);
      onDone?.();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Acknowledgement failed');
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <Text type="success">
        Acknowledgement recorded
        {acknowledgedByName || name ? ` (${acknowledgedByName || name})` : ''}.
      </Text>
    );
  }

  return (
    <div>
      <Text strong>Supplier acknowledgement</Text>
      <div style={{ marginTop: 8, marginBottom: 8 }}>
        <Text type="secondary">
          Sign below to confirm you received this market-purchase request. Optionally attach supporting
          documents.
        </Text>
      </div>
      <Input
        placeholder="Your full name *"
        value={name}
        onChange={(e) => setName(e.target.value)}
        style={{ marginBottom: 8, maxWidth: 360 }}
      />
      <div
        style={{
          border: '1px solid #d9d9d9',
          borderRadius: 8,
          overflow: 'hidden',
          maxWidth: 480,
          touchAction: 'none',
        }}
      >
        <canvas
          ref={canvasRef}
          width={480}
          height={160}
          style={{ display: 'block', width: '100%', background: '#fff', cursor: 'crosshair' }}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
      </div>
      <Space style={{ marginTop: 8, marginBottom: 8 }}>
        <Button size="small" onClick={clear}>
          Clear signature
        </Button>
      </Space>
      <div style={{ marginBottom: 8 }}>
        <Upload
          beforeUpload={() => false}
          fileList={files}
          onChange={({ fileList }) => setFiles(fileList.slice(0, 5))}
          accept=".pdf,image/*"
          multiple
        >
          <Button icon={<UploadOutlined />} size="small">
            Upload documents
          </Button>
        </Upload>
      </div>
      <Input.TextArea
        rows={2}
        placeholder="Notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        style={{ marginBottom: 8, maxWidth: 480 }}
      />
      {error ? <div style={{ color: '#cf1322', marginBottom: 8 }}>{error}</div> : null}
      <Button type="primary" onClick={submit} loading={saving}>
        Submit acknowledgement
      </Button>
    </div>
  );
}
